// run_yaml_to_release.py encodes an rc-tagged ccm_autonomy (e.g. "11.3.rc1")
// as version_number "11.3.91" (patch "9" + rc digits), and an m-tagged
// master build (e.g. "11.4.m3") as version_number "11.4.83" (patch "8" +
// build digits), since version_number must be strict d.d.d (DEV-5520).
//
// The encoded patch number alone can't be trusted to reverse this: a
// component from an ordinary, non-rc/non-master release could coincidentally
// have a real patch number in the 80-99 range, and pattern-matching the
// version number in isolation would wrongly relabel it as a test build
// (PR #1140 review). Not every component in a release is guaranteed to
// share one uniformly-derived version either -- only ones produced by
// run_yaml_to_release.py are; components added independently (Bulk Create
// Versions, Add Version) can carry their own, unrelated version numbers in
// the same release. So instead of guessing from a single component's
// number, this reads the release's own ccm_autonomy (the reliable source of
// the tag; a release's free-form display name is only a fallback) and only
// decodes a component whose version exactly matches what that specific
// value actually encodes -- narrow enough that an unrelated component would
// have to coincidentally carry that exact version to be mislabeled.

export interface ReleaseVersionTag {
  // The exact version_number this tag encodes to, e.g. "11.4.83".
  encodedVersion: string;
  // The human-readable form to show instead, e.g. "11.4.m3".
  display: string;
}

const RELEASE_TAG_PATTERN = /(\d+)\.(\d+)\.(rc|m)([1-9]\d*)/;

// `source` is whatever string might embed the tag -- typically the
// release's ccm_autonomy (e.g. "11.4.m3"), with its free-form display name
// as a fallback for the yaml-generated case where the name embeds it too.
export function parseReleaseVersionTag(
  source: string | null | undefined
): ReleaseVersionTag | null {
  if (!source) return null;
  const match = source.match(RELEASE_TAG_PATTERN);
  if (!match) return null;
  const [, major, minor, kind, digits] = match;
  const prefixDigit = kind === "rc" ? "9" : "8";
  return {
    encodedVersion: `${major}.${minor}.${prefixDigit}${digits}`,
    display: `${major}.${minor}.${kind}${digits}`,
  };
}

export function formatComponentVersion(
  versionNumber: string,
  releaseTag: ReleaseVersionTag | null
): { display: string; actual: string | null } {
  if (!releaseTag || versionNumber !== releaseTag.encodedVersion) {
    return { display: versionNumber, actual: null };
  }
  return { display: releaseTag.display, actual: versionNumber };
}
